import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { chevronBackOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { Preferences } from '@capacitor/preferences';
import { IonButton, IonContent, IonPage, IonIcon, useIonRouter } from '@ionic/react';
import foodfightLogo from '../../assets/foodfight-logo.png';
import { AppDispatchContext, ACTIONS } from '../../global/AppContext';
import { confirmCode, resendCode, signin, checkUnconfirmedAndSignUp } from '../../global/request/auth';
import { updateProfile, uploadUserIcon } from '../../global/request/user';
import { identifyUser, trackEvent } from '../../utils/customerio';
import '../../styles/authentication/SignUp.scss';
import MessageOverlay from '../fight/MessageOverlay';
import { AuthContext, AuthDispatchContext } from './AuthContext';
import ProfilePictureUpload from './ProfilePictureUpload';
import SignupField from './SignupField';
import SixDigitInput from './SixDigitInput';

function SignUp() {
  const router = useIonRouter();

  const auth = useContext(AuthContext);
  const authCtx = useContext(AuthDispatchContext);
  const appCtx = useContext(AppDispatchContext);

  const overlayRef = useRef();
  const [overlayMsg, setOverlayMsg] = useState('');

  const [firstname, setFirstname] = useState(null);
  const [lastname, setLastname] = useState(null);
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState(null);
  const [formattedPhone, setFormattedPhone] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  // const [privacypolicy, setPrivacyPolicy] = useState(false);
  // const [tos, setTos] = useState(false);
  const [showConfirmCode, setShowConfirmCode] = useState(false);
  const [code, setCode] = useState(null);
  const [isLongEnough, setIsLongEnough] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasLetter, setHasLetter] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [referralId, setReferralId] = useState('');
  const [referralFoodFightId, setReferralFoodFightId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const referalId = queryParams.get('referral_id');
    const foodfightId = queryParams.get('foodfight_id');
    if (referalId) {
      setReferralId(referalId);
    }
    if (foodfightId) {
      setReferralFoodFightId(foodfightId);
    }
  }, [referralId]);

  useEffect(() => {
    if (auth.loggedIn && referralFoodFightId) {
      if (referralFoodFightId) {
        appCtx.dispatch({ action: ACTIONS.SET_REFERRAL_FOODFIGHT, payload: referralFoodFightId });
        localStorage.setItem('referralFoodFightId', referralFoodFightId);
      }
      router.push('/app/home');
    }
  }, [appCtx, auth, referralFoodFightId, router]);

  const isValidEmail = emailAddress => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);
  };

  const isValidPassword = userPassword => {
    const minLength = 8;
    return userPassword.length >= minLength;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      // TODO if email not valid
      return;
    }
    let signupResponse;
    try {
      // eslint-disable-next-line no-unused-vars
      signupResponse = await checkUnconfirmedAndSignUp({
        email,
        password,
        phone,
        username,
      });
      setShowConfirmCode(true);
    } catch (error) {
      setOverlayMsg(error.message);
      overlayRef.current.present();
    }
  };
  const handleConfirmCode = async event => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await confirmCode({
        email,
        password,
        code,
      });

      const response = await signin({
        email,
        password,
      });
      authCtx.dispatch({ action: 'storeAuthResult', authResult: response });

      // Get user info for Customer.io
      const userId = response.idData.sub;
      const displayName = response.idData.email;

      // Identify new user in Customer.io
      identifyUser(userId, {
        email: displayName,
        name: `${firstname} ${lastname}`,
        username,
        phone_number: phone,
        referral_id: referralId,
        signupMethod: 'email',
        signupDate: new Date().toISOString(),
      });

      // Track signup event
      trackEvent('user_signup', {
        method: 'email',
        userId,
        hasReferral: !!referralId,
      });

      let iconUrl = '';
      if (selectedImage) {
        try {
          const iconResponse = await uploadUserIcon(response.tokens.idToken, selectedImage);
          iconUrl = iconResponse.icon;
        } catch (error) {
          console.error('Failed to upload profile picture:', error);
        }
      }

      await updateProfile(response.tokens.idToken, {
        addresses: [],
        address_set: false,
        birthdate: '',
        delivery_restaurant: '',
        delivery_restaurant_id: '',
        fav_sports: '',
        icon: iconUrl,
        user_id: response.tokens.idToken,
        // eslint-disable-next-line prefer-template
        name: firstname + ' ' + lastname,
        phone_number: phone,
        username,
        referral_id: referralId,
        email,
        friends: [],
        // storing invalid latitude and longitude numbers upon creation
        latitude: 100.0,
        longitude: 200.0,
        devices: [],
        sent_review: false,
      });

      await Preferences.set({ key: 'showVenueModal', value: 'true' });
      if (referralFoodFightId) {
        appCtx.dispatch({ action: ACTIONS.SET_REFERRAL_FOODFIGHT, payload: referralFoodFightId });
        localStorage.setItem('referralFoodFightId', referralFoodFightId);
      }
      router.push('/app/home');
    } catch (error) {
      console.error(error);
      setOverlayMsg(error.message);
      overlayRef.current.present();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async e => {
    e.preventDefault();
    await resendCode({
      email,
    });
    setOverlayMsg('Verification code has been resent!');
    overlayRef.current.present();
  };

  const handlePhoneNumber = inputNumber => {
    // eslint-disable-next-line prefer-template
    const cleaned = '' + inputNumber;
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return `(${match[1]}) ${match[2]}****`;
  };

  const handleButtonClick = () => {
    const phoneNumber = handlePhoneNumber(phone);
    setFormattedPhone(phoneNumber);
  };

  const handleCodeInput = processedCode => {
    setCode(processedCode);
  };

  const handleFocusUsername = useCallback(() => {
    if (username === null && firstname && lastname) {
      setUsername(firstname[0] + lastname);
    }
  }, [username, firstname, lastname]);

  const handleImageSelect = useCallback(file => {
    setSelectedImage(file);
  }, []);

  useEffect(() => {
    setIsLongEnough(password.length >= 8);
    setHasNumber(/\d/.test(password));
    setHasLetter(/[A-Z]/.test(password));
    setPasswordsMatch(password && confirmPassword && password === confirmPassword);
  }, [password, confirmPassword]);

  useEffect(() => {
    if (password === '' && confirmPassword === '') {
      setIsLongEnough(false);
      setHasNumber(false);
      setHasLetter(false);
      setPasswordsMatch(false);
    }
  }, [password, confirmPassword]);

  const getClassIfTrue = (condition, hasValue) => {
    if (!hasValue) return 'item';
    return condition ? 'item invisible' : 'item';
  };

  const hasUnmetRequirements =
    (password.length === 0 && confirmPassword.length === 0) ||
    (password.length > 0 && (!isLongEnough || !hasNumber || !hasLetter)) ||
    ((password.length > 0 || confirmPassword.length > 0) && !passwordsMatch);

  const handleBackButton = () => {
    if (referralFoodFightId) {
      appCtx.dispatch({ action: ACTIONS.SET_REFERRAL_FOODFIGHT, payload: referralFoodFightId });
      localStorage.setItem('referralFoodFightId', referralFoodFightId);
    }
    router.push('/signin', 'back');
  };

  return (
    <IonPage>
      <IonContent id='signup-page'>
        {!showConfirmCode ? (
          <div id='signup-content'>
            <div className='signup-nav-header'>
              <IonButton className='signup-back-button' onClick={handleBackButton} fill='clear'>
                <IonIcon slot='icon-only' icon={chevronBackOutline} className='signup-back-icon' />
                Already have an account? Sign In.
              </IonButton>
              <span className='signup-nav-title'>Create New Account</span>
            </div>
            <h2>Create an Account</h2>
            <ProfilePictureUpload onImageSelect={handleImageSelect} selectedImage={selectedImage} />
            <form id='entry-options' onSubmit={handleSubmit}>
              <SignupField label='First Name' type='text' setter={setFirstname} value={firstname} />
              <SignupField label='Last Name' type='text' setter={setLastname} value={lastname} />
              <SignupField
                label='Username'
                type='text'
                setter={setUsername}
                value={username}
                onFocus={handleFocusUsername}
              />
              <SignupField label='Email' type='email' setter={setEmail} value={email} />
              <SignupField
                label='Phone Number'
                type='tel'
                value={phone}
                setter={val => setPhone(val.replace(/\D/g, '').slice(0, 10))}
              />
              <div style={{ position: 'relative' }}>
                <SignupField
                  label='Password'
                  type={showPassword ? 'text' : 'password'}
                  setter={setPassword}
                  value={password}
                />
                <IonIcon
                  className='password-toggle-icon'
                  icon={showPassword ? eyeOffOutline : eyeOutline}
                  onClick={() => setShowPassword(v => !v)}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <SignupField
                  label='Confirm Password'
                  type={showConfirmPassword ? 'text' : 'password'}
                  setter={setConfirmPassword}
                  value={confirmPassword}
                />
                <IonIcon
                  className='password-toggle-icon'
                  icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                  onClick={() => setShowConfirmPassword(v => !v)}
                />
              </div>
              <div className={`password-req${hasUnmetRequirements ? ' has-unmet-requirements' : ''}`}>
                <ul>
                  <li
                    id='password-capital'
                    className={password.length === 0 ? 'item' : getClassIfTrue(hasLetter, password.length > 0)}>
                    Password needs to be least one capital letter
                  </li>
                  <li
                    id='password-length'
                    className={password.length === 0 ? 'item' : getClassIfTrue(isLongEnough, password.length > 0)}>
                    Password needs to be at least 8 characters long
                  </li>
                  <li
                    id='password-number'
                    className={password.length === 0 ? 'item' : getClassIfTrue(hasNumber, password.length > 0)}>
                    Password needs to contain at least one number
                  </li>
                  <li
                    id='password-match'
                    className={
                      password.length === 0 && confirmPassword.length === 0
                        ? 'item'
                        : getClassIfTrue(passwordsMatch, password.length > 0 || confirmPassword.length > 0)
                    }>
                    Passwords needs to match
                  </li>
                </ul>
              </div>
              {/* <div id="agreements">
                TODO: add links to tos and privacy policy
                <div>
                  <IonCheckbox name="tos" checked={tos} onIonChange={() => setTos(!tos)} />
                  <IonLabel>
                    I agree to <a href="https://www.getfoodfight.com/">FoodFight&apos;s Terms and Services</a>
                  </IonLabel>
                </div>
                <div>
                  <IonCheckbox
                    name="privacypolicy"
                    checked={privacypolicy}
                    onIonChange={() => setPrivacyPolicy(!privacypolicy)}
                  />
                  <IonLabel>
                    I agree to <a href="https://www.getfoodfight.com/">FoodFight&apos;s Privacy Policy</a>
                  </IonLabel>
                </div>
              </div> */}
              <IonButton
                id='signup-btn'
                expand='block'
                type='submit'
                fill='outline'
                onClick={handleButtonClick}
                disabled={
                  !(
                    firstname &&
                    lastname &&
                    username &&
                    isValidEmail(email) &&
                    isValidPassword(password) &&
                    passwordsMatch &&
                    phone &&
                    phone.length === 10
                  )
                }>
                Create an Account
              </IonButton>
            </form>
          </div>
        ) : (
          <div id='signup-content'>
            <div className='signup-nav-header'>
              <IonButton className='signup-back-button' onClick={() => setShowConfirmCode(false)} fill='clear'>
                <IonIcon slot='icon-only' icon={chevronBackOutline} className='signup-back-icon' />
              </IonButton>
              <span className='signup-nav-title'>Enter verification code</span>
            </div>
            <img src={foodfightLogo} alt='FoodFight Logo' className='signup-logo' />
            <p className='verification-remark'>Please verify your phone number by entering code below:</p>
            <p>Code is sent to: {formattedPhone}</p>
            <form id='entry-options' onSubmit={handleConfirmCode}>
              <SixDigitInput onCodeChange={handleCodeInput} />
              <p className='verification-remark'>
                <b> DO NOT </b> share the code with others
              </p>
              <div className='code-btn-wrapper'>
                <IonButton
                  className='verify'
                  expand='block'
                  type='submit'
                  fill='outline'
                  disabled={!(code && code.length === 6)}>
                  Verify Account
                </IonButton>

                <IonButton className='resend-code-btn' expand='block' fill='outline' onClick={handleResendCode}>
                  Resend Code
                </IonButton>
              </div>
            </form>
            <div className='question-wrapper'>
              <p className='condition-text'>
                Need Help?
                <a className='contact-us-link' href='https://www.getfoodfight.com/'>
                  Contact FoodFight
                </a>
              </p>
            </div>
          </div>
        )}
        <MessageOverlay blur ref={overlayRef} msg={overlayMsg} />
      </IonContent>
    </IonPage>
  );
}

export default SignUp;
